import { useQuery } from '@tanstack/react-query';
import { tenant } from '../../lib/tenant';
import type { ModuleRoute } from '../../routes/modules';

type Badge = NonNullable<ModuleRoute['badge']>;

/**
 * Compteur d'un onglet de module (ajout 23/09, parcours factures) — ex.
 * « 2 » en rouge sur Commandes = deux commandes « problème en cours ».
 *
 * Lecture seule, même session que le reste (cookie). Plusieurs onglets qui
 * lisent la même `url` partagent UNE requête (clé de cache = url). Rafraîchi
 * toutes les 60 s. En cas d'échec ou de 0 : rien n'est affiché — un compteur
 * absent ne ment pas, un compteur faux oui.
 */
export function CompteurOnglet({ badge }: { readonly badge: Badge }): JSX.Element | null {
  const q = useQuery({
    queryKey: ['shell', 'compteur-onglet', badge.url],
    queryFn: async ({ signal }) => {
      const res = await fetch(badge.url, {
        headers: { Accept: 'application/json', 'X-Tenant-Id': tenant.id },
        credentials: 'include',
        signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as Record<string, unknown>;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  });
  const brut = q.data?.[badge.champ];
  const n = typeof brut === 'number' && Number.isFinite(brut) ? brut : 0;
  if (n <= 0) return null;
  return (
    <span
      data-testid="compteur-onglet"
      aria-label={`${n} à traiter`}
      className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[11px] font-bold tabular-nums"
      style={
        badge.ton === 'danger'
          ? { background: 'var(--danger-soft)', color: 'var(--danger-fg)' }
          : { background: 'var(--warn-soft)', color: 'var(--warn-fg)' }
      }
    >
      {n}
    </span>
  );
}
